import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Result,
  Row,
  Spin,
  Typography,
  message,
} from 'antd';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import logo from 'themes/default/logo-full.svg';
import { getErrorMessages } from 'app/utils/functions';
import { getCities, getWards } from 'modules/Profile/service';
import styles from 'modules/Exam/Screens/Exam.module.less';
import { getProgramHtml } from 'modules/AdminTsaTrialExam/defaultProgramHtml';
import { buildRegistrationPayload } from '../buildRegistrationPayload';
import { getTsaTrialExamPeriod, registerTsaTrialExam } from '../service';
import TsaTrialExamFormFields from '../Components/TsaTrialExamFormFields';

const { Title, Paragraph } = Typography;

const TsaTrialExamRegister = ({ user }) => {
  const { slug } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [period, setPeriod] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [wards, setWards] = useState([]);

  const isLoggedIn = user && Object.keys(user).length > 0;
  const formConfig = period?.form_config || {};
  const messages = formConfig.messages || {};

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [periodRes, provinceRes] = await Promise.all([
          getTsaTrialExamPeriod(slug),
          getCities(1),
        ]);

        if (periodRes.data.code === 200) {
          setPeriod(periodRes.data.data.period);
        }

        if (provinceRes.data.code === 200) {
          setProvinces(
            provinceRes.data.data.cities.map((i) => ({
              value: i.code || i.provinceid,
              label: i.name,
            })),
          );
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  useEffect(() => {
    if (isLoggedIn && user.email) {
      form.setFieldsValue({ account_email: user.email });
    }
  }, [form, isLoggedIn, user]);

  const onProvinceChange = async (provinceId) => {
    form.setFieldsValue({ school_ward: undefined });
    setWards([]);
    if (!provinceId) {
      return;
    }
    const response = await getWards(provinceId);
    if (response.data.code === 200) {
      setWards(
        response.data.data.wards.map((i) => ({
          value: i.code || i.wardid,
          label: i.name,
        })),
      );
    }
  };

  const onFinish = async (values) => {
    if (!period?.is_open) {
      message.error(messages.period_closed || 'Đợt đăng ký hiện không khả dụng');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildRegistrationPayload(
        slug,
        values,
        formConfig.fields || [],
      );

      const response = await registerTsaTrialExam(payload);
      if (response.data.code === 200) {
        setSubmitted(true);
        message.success(response.data.message);
      } else {
        message.error(getErrorMessages(response.data));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.main}>
        <div className="text-center p-5">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!slug || !period) {
    return (
      <div className={styles.main}>
        <Result
          status="404"
          title="Không tìm thấy trang"
          subTitle="Liên kết không hợp lệ hoặc đợt đăng ký không tồn tại."
        />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.main}>
        <Row className={styles.container}>
          <Col xs={24}>
            <Result
              status="success"
              title={messages.success_title || 'Đăng ký thành công'}
              subTitle={messages.success_subtitle || ''}
            />
          </Col>
        </Row>
      </div>
    );
  }

  const programHtml = getProgramHtml(period.program_details);
  const closedSuffix = (messages.closed_suffix || '').replace(
    '{deadline}',
    period.registration_deadline
      ? moment(period.registration_deadline).format('DD/MM/YYYY HH:mm')
      : '',
  );

  return (
    <div className={styles.main}>
      <Row className={styles.container}>
        <Col xs={24} className="mt-6">
          <div className="text-center">
            <img className="logo-kt mb-4 mt-4" alt="TSA HUST" src={logo} />
          </div>
          <div className={styles.body}>
            <div className={styles.title}>
              <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
                {period.title}
              </Title>
            </div>
            <div className={styles.section}>
              {period.intro && (
                <div
                  className={`${styles.tsaTrialRichHtml} mb-3`}
                  dangerouslySetInnerHTML={{ __html: period.intro }}
                />
              )}

              {programHtml && (
                <div
                  className={`${styles.tsaTrialRichHtml} mb-4`}
                  dangerouslySetInnerHTML={{ __html: programHtml }}
                />
              )}

              {!period.is_open && (
                <Paragraph type="danger">
                  {messages.closed || 'Đợt đăng ký đã đóng'}
                  {closedSuffix ? ` ${closedSuffix}` : ''}.
                </Paragraph>
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark
                className="mt-4"
                disabled={!period.is_open}
              >
                <TsaTrialExamFormFields
                  fields={formConfig.fields || []}
                  period={period}
                  provinces={provinces}
                  wards={wards}
                  onProvinceChange={onProvinceChange}
                  isLoggedIn={isLoggedIn}
                  formDisabled={!period.is_open}
                />

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    disabled={!period.is_open}
                    block
                  >
                    {messages.submit_button || 'Gửi đăng ký'}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(TsaTrialExamRegister);
